export const TRIGGERS = [
  'ActivateMain',
  'WhenAttacking',
  'Trigger',
  'Rush',
  'Blocker',
  'Banish',
  'DoubleAttack',
  'OnPlay',
  'Passive',
  'Counter',
  'EndOfBattle',
  'EndOfYourTurn',
  'OnBlock',
  'PlayerActivatedEvent',
  'OpponentActivatedEvent',
  'OnKO',
  'OpponentsCharacterKOd',
  'CanAttackActiveCharacters',
  'CannotBeKod',
  'CannotBeAttacked',
  'CannotAttack',
  'ExtraName',
];

export const RESTRICTIONS = ['', 'OncePerTurn'];

export const EFFECT_FORMATS = [
  { id: 'keyword', label: 'Keyword only (Blocker, Rush, …)' },
  { id: 'passive', label: 'Passive effect' },
  { id: 'steps', label: 'Effect steps (v2)' },
];

export const PRE_CONDITION_CHECKS = [
  { id: 'minAttachedDon', label: 'minAttachedDon', fields: ['count'] },
  { id: 'hasActiveDon', label: 'hasActiveDon', fields: ['zone', 'count'] },
  { id: 'hasRestedDon', label: 'hasRestedDon', fields: ['zone', 'count'] },
  { id: 'minFieldCharacters', label: 'minFieldCharacters', fields: ['scope', 'count', 'filters'], filtersLabel: 'Character filters', filtersHint: 'Optional. Leave empty to count all field characters.' },
  { id: 'hasNoFieldCharacters', label: 'hasNoFieldCharacters', fields: ['scope', 'filters'], filtersLabel: 'Character filters', filtersHint: 'Optional. Leave empty to require no field characters at all.' },
  { id: 'totalDons', label: 'totalDons', fields: ['count'] },
  { id: 'minLife', label: 'minLife', fields: ['count'] },
  { id: 'leaderName', label: 'leaderName', fields: ['name'] },
  {
    id: 'leaderMatchesFilter',
    label: 'leaderMatchesFilter',
    fields: ['filters'],
    filtersLabel: 'Leader filters',
    filtersHint: "Player's leader must match these filters. Leave empty to pass automatically.",
  },
  { id: 'hasCardsInHand', label: 'hasCardsInHand', fields: ['count', 'filters'], countLabel: 'Min count', filtersLabel: 'Hand card filters', filtersHint: 'Optional. Leave empty to count all cards in hand. Passes when count is at least this value.' },
  { id: 'maxCardsInHand', label: 'maxCardsInHand', fields: ['count', 'filters'], countLabel: 'Max count', filtersLabel: 'Hand card filters', filtersHint: 'Optional. Leave empty to count all cards in hand. Passes when count is at most this value.' },
  { id: 'thisCardIsActive', label: 'thisCardIsActive', fields: [] },
  { id: 'thisCardIsRested', label: 'thisCardIsRested', fields: [] },
  { id: 'yourTurn', label: 'yourTurn', fields: [] },
  { id: 'opponentTurn', label: 'opponentTurn', fields: [] },
];

export const ZONES = [
  'Hand',
  'Leader',
  'FieldCharacters',
  'CostArea',
  'Deck',
  'Trash',
  'Life',
];

export const SCOPES = ['Player', 'Opponent'];

/** Passive effectDetails.scope (matches passiveEffectService.js). */
export const PASSIVE_SCOPES = ['Player', 'Opponent', 'Both'];

/** Target selection scopes (matches findValidTargets in effectService.js). */
export const TARGET_SCOPES = ['Player', 'Opponent', 'Both'];

export const FILTER_COMPARISONS = [
  'eq',
  'neq',
  'neqRef',
  'lte',
  'gte',
  'contains',
  'oneOf',
  'notContainsRef',
];

export const FILTER_REF_COMPARISONS = ['neqRef', 'notContainsRef'];

/** Card type values for filter field `type`. */
export const FILTER_CARD_TYPES = ['Character', 'Event', 'Leader'];

/** Card zone `location` values (matches CardInstance.location on the server). */
export const FILTER_CARD_LOCATIONS = [
  'Hand',
  'Deck',
  'Life',
  'Leader',
  'field_characters',
  'field_stages',
  'trash',
  'attached',
  'active_don',
  'don_deck',
];

export const FILTER_FIELDS = [
  'type',
  'cost',
  'power',
  'color',
  'cardTraits',
  'name',
  'location',
  'instanceId',
  'isSelf',
  'isRested',
  'isBlocker',
];

export const BOOLEAN_FILTER_FIELDS = ['isSelf', 'isRested', 'isBlocker'];

/** Stat values for passive StatModifier `modifications[].stat`. */
export const PASSIVE_MODIFIER_STATS = ['power', 'cost'];

export const PASSIVE_EFFECT_TYPES = [
  { id: 'StatModifier', label: 'Stat modifier' },
  { id: 'AddEffect', label: 'Add effect' },
];

export const createPassiveEffectDetails = (type = 'StatModifier') => {
  const base = {
    type,
    scope: 'Player',
    filters: [{ field: 'isSelf', value: true }],
  };
  if (type === 'AddEffect') {
    return { ...base, effect: { trigger: 'Rush' } };
  }
  return {
    ...base,
    modifications: [{ stat: 'power', amount: 1000 }],
    multipliers: [],
  };
};

export const CANNOT_BE_KOD_TYPES = [
  { id: '', label: '(none)' },
  { id: 'ByAttack', label: 'By attack' },
];

export const createCannotBeKodDetails = (type = '') => ({
  type,
  attackerFilters: [],
});

const applyCannotBeKodFields = (result, cannotBeKodDetails) => {
  if (!cannotBeKodDetails?.type) return result;
  result.type = cannotBeKodDetails.type;
  if (cannotBeKodDetails.type === 'ByAttack') {
    result.attackerFilters = (cannotBeKodDetails.attackerFilters || []).map(buildFilter);
  }
  return result;
};

export const createExtraNameDetails = (name = '') => ({ name });

const applyExtraNameFields = (result, extraNameDetails) => {
  const name = extraNameDetails?.name?.trim();
  if (!name) return result;
  result.details = { name };
  return result;
};

/** Passive StatModifier multiplier types. */
export const PASSIVE_MULTIPLIER_TYPES = [
  { id: 'forEvery', label: 'forEvery — per N matching cards' },
];

export const createForEveryMultiplier = () => ({
  type: 'forEvery',
  scope: 'Player',
  zone: ['Hand'],
  amount: 1,
  filters: [],
});

export const formatZonesForEditor = (zone) => {
  if (typeof zone === 'string') return zone;
  return (Array.isArray(zone) ? zone : zone ? [zone] : []).join(', ');
};

export const parseZonesFromEditor = (zone) => {
  if (Array.isArray(zone)) return zone.map((z) => String(z).trim()).filter(Boolean);
  const text = String(zone ?? '').trim();
  if (!text) return [];
  if (text.includes(',')) {
    return text.split(',').map((z) => z.trim()).filter(Boolean);
  }
  return [text];
};

export const normalizeZonesForEditor = (zone) => parseZonesFromEditor(zone);

export const createPassiveMultiplier = (type = 'forEvery') => {
  if (type === 'forEvery') return createForEveryMultiplier();
  return createForEveryMultiplier();
};

export const importPassiveMultiplier = (raw = {}) => {
  if (raw.type === 'forEvery') {
    return {
      type: 'forEvery',
      scope: raw.scope || 'Player',
      zone: normalizeZonesForEditor(raw.zone),
      amount: raw.amount ?? 1,
      filters: importFiltersForEditor(raw.filters),
    };
  }
  return createForEveryMultiplier();
};

export const buildPassiveMultiplier = (multiplier) => {
  if (multiplier.type !== 'forEvery') return null;
  const zone = parseZonesFromEditor(multiplier.zone);
  const result = {
    type: 'forEvery',
    scope: multiplier.scope || 'Player',
    zone,
    amount: Number(multiplier.amount) || 1,
  };
  if (multiplier.filters?.length > 0) {
    result.filters = multiplier.filters.map(buildFilter);
  }
  return result;
};

export const isBooleanFilterField = (field) => BOOLEAN_FILTER_FIELDS.includes(field);

const parseBooleanFilterValue = (value, defaultValue = true) => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return defaultValue;
};

export const STEP_TYPES = [
  { id: 'target', label: 'Target selection' },
  { id: 'action', label: 'Action' },
  { id: 'if', label: 'Conditional (if)' },
  { id: 'searchDeck', label: 'Search deck' },
  { id: 'returnDons', label: 'Return DON!!' },
  { id: 'prompt', label: 'Prompt (confirm/pass)' },
  { id: 'reveal', label: 'Reveal cards' },
];

export const ACTION_TYPES = [
  'ModifyPower',
  'DrawCards',
  'PlayThisCard',
  'PlayCards',
  'KO',
  'AttachDonV2',
  'RestDons',
  'RestThisCard',
  'RestCards',
  'SetCardsAsActive',
  'SetThisAsActive',
  'TrashThisCard',
  'TrashCards',
  'ReturnToOwnerHand',
  'ReturnToOwnerDeck',
  'ReturnLifeToOwnerDeck',
  'ReturnDonsToDonDeck',
  'AddDon',
  'ActivateDons',
  'ShuffleDeck',
  'SearchDeck',
  'AddEffect',
  'AddBlockerRestriction',
  'TrashLife',
  'TakeLife',
  'NoOp',
];

/** Detail keys that reference a prior step stepId (target, searchDeck, returnDons, etc.). */
export const ACTION_DETAIL_REF_KEYS = [
  'targetCardIdRefs',
  'targetCardStepIdRef',
  'targetCardIdRef',
  'cardIdsRef',
  'donInstanceIdRef',
];

export const ACTION_DETAIL_REF_LABELS = {
  targetCardIdRefs: 'Target step',
  targetCardStepIdRef: 'Recipient target step',
  targetCardIdRef: 'Target step',
  cardIdsRef: 'Search deck step',
  donInstanceIdRef: 'DON selection step',
};

/** Non-stepId values allowed on ref fields (e.g. source card). */
export const ACTION_DETAIL_REF_SPECIAL_VALUES = {
  targetCardIdRefs: [{ value: 'thisCard', label: 'thisCard (source card)' }],
};

/** Per-action extra ref special values (merged with ACTION_DETAIL_REF_SPECIAL_VALUES). */
export const ACTION_TYPE_REF_SPECIAL_VALUES = {
  KO: {
    targetCardIdRefs: [{
      value: 'allCharactersOtherThanThisCard',
      label: 'allCharactersOtherThanThisCard (all field characters except source)',
    }],
  },
};

export const getActionDetailRefSpecialValues = (actionType, key) => [
  ...(ACTION_DETAIL_REF_SPECIAL_VALUES[key] || []),
  ...(ACTION_TYPE_REF_SPECIAL_VALUES[actionType]?.[key] || []),
];

export const REF_SELECTABLE_STEP_TYPES = ['target', 'searchDeck', 'returnDons'];

/** Step types shown per action detail ref key (defaults include target, searchDeck, returnDons). */
export const ACTION_DETAIL_REF_STEP_TYPES = {
  cardIdsRef: ['target', 'searchDeck'],
  donInstanceIdRef: ['returnDons'],
};

export const DEFAULT_ACTION_REF_STEP_TYPES = ['target', 'searchDeck', 'returnDons'];

export const getActionDetailRefStepTypes = (refKey) =>
  ACTION_DETAIL_REF_STEP_TYPES[refKey] || DEFAULT_ACTION_REF_STEP_TYPES;

export const isActionDetailRefKey = (key) =>
  ACTION_DETAIL_REF_KEYS.includes(key) || /Ref(s)?$/.test(key);

export const getRefSelectableSteps = (steps, stepTypes = ['target', 'searchDeck']) =>
  steps
    .filter((s) => stepTypes.includes(s.type))
    .map((s) => ({
      stepId: s.stepId || '',
      type: s.type,
      label: `${s.stepId || '(no id)'} (${s.type})`,
    }));

/** Deck placement for ReturnToOwnerDeck / ReturnLifeToOwnerDeck (PlayerState.moveCard). */
export const DECK_POSITIONS = ['Top', 'Bottom'];

/** Duration values used by ModifyPower, AddBlockerRestriction, etc. */
export const ACTION_DURATION_OPTIONS = ['EndOfTurn', 'EndOfBattle'];

/** Duration values for AddEffect (matches addEffect.js). */
export const ADD_EFFECT_DURATION_OPTIONS = ['EndOfTurn', 'EndOfBattle', 'EndOfOpponentNextTurn'];

export const getActionDurationOptions = (actionType) =>
  (actionType === 'AddEffect' ? ADD_EFFECT_DURATION_OPTIONS : ACTION_DURATION_OPTIONS);

/** Keyword triggers grantable via AddEffect (matches cardService keyword checks). */
export const ADD_EFFECT_KEYWORD_TRIGGERS = [
  'Rush',
  'Blocker',
  'Banish',
  'DoubleAttack',
  'CanAttackActiveCharacters',
  'CannotBeKod',
  'CannotBeAttacked',
  'CannotAttack',
  'ExtraName',
];

export const actionDetailHasDuration = (actionType, details = {}) => {
  const template = ACTION_DETAIL_TEMPLATES[actionType] || {};
  return Object.prototype.hasOwnProperty.call(template, 'duration')
    || Object.prototype.hasOwnProperty.call(details, 'duration');
};

/** Per-action detail keys edited outside the JSON textarea. */
export const ACTION_STRUCTURED_DETAIL_KEYS = {
  ReturnToOwnerDeck: ['position'],
  ReturnLifeToOwnerDeck: ['scope', 'amount', 'position'],
  AddDon: ['count', 'isRested'],
  AddBlockerRestriction: ['filters'],
  AddEffect: ['effect'],
};

export const getActionStructuredDetailKeys = (actionType, details = {}) => {
  const keys = [...(ACTION_STRUCTURED_DETAIL_KEYS[actionType] || [])];
  if (actionDetailHasDuration(actionType, details)) keys.push('duration');
  return keys;
};

const getStructuredDetailKeys = getActionStructuredDetailKeys;

export const stripActionDetailRefs = (details = {}) => {
  const rest = { ...details };
  for (const key of Object.keys(rest)) {
    if (isActionDetailRefKey(key)) delete rest[key];
  }
  return rest;
};

export const stripStructuredActionDetails = (details = {}, actionType = '') => {
  const rest = stripActionDetailRefs(details);
  for (const key of getStructuredDetailKeys(actionType, details)) {
    delete rest[key];
  }
  return rest;
};

export const getActionDetailRefKeys = (details = {}, actionType = '') => {
  const template = ACTION_DETAIL_TEMPLATES[actionType] || {};
  return [...new Set([
    ...Object.keys(template).filter(isActionDetailRefKey),
    ...Object.keys(details).filter(isActionDetailRefKey),
  ])];
};

export const parseActionDetailsSafe = (json) => {
  try {
    return parseDetailsJson(json || '{}') ?? {};
  } catch {
    return null;
  }
};

export const ACTION_DETAIL_TEMPLATES = {
  ModifyPower: { amount: 1000, duration: 'EndOfTurn', targetCardIdRefs: 'chooseTarget' },
  DrawCards: { amount: 1 },
  PlayThisCard: {},
  PlayCards: { cardIdsRef: 'searchDeck' },
  KO: { targetCardIdRefs: 'chooseTarget' },
  AttachDonV2: { targetCardStepIdRef: 'chooseRecipient', isRested: true, count: 1 },
  RestDons: { amount: 1 },
  RestThisCard: {},
  RestCards: { targetCardIdRefs: 'chooseTarget' },
  SetCardsAsActive: { targetCardIdRefs: 'chooseTarget' },
  SetThisAsActive: {},
  TrashThisCard: {},
  TrashCards: { targetCardIdRefs: 'chooseTarget' },
  ReturnToOwnerHand: { targetCardIdRefs: 'chooseTarget' },
  ReturnToOwnerDeck: { targetCardIdRefs: 'chooseTarget', position: 'Bottom' },
  ReturnLifeToOwnerDeck: { scope: 'Opponent', amount: 1, position: 'Bottom' },
  ReturnDonsToDonDeck: { donInstanceIdRef: 'chooseDons' },
  AddDon: { count: 1, isRested: false },
  ActivateDons: { amount: 1 },
  ShuffleDeck: { scope: 'Player' },
  AddBlockerRestriction: { duration: 'EndOfBattle', filters: [] },
  AddEffect: {
    duration: 'EndOfTurn',
    targetCardIdRefs: 'chooseTarget',
    effect: { trigger: 'Rush' },
  },
  TrashLife: { amount: 1 },
  TakeLife: { amount: 1 },
};

export const createPreCondition = (check = 'minAttachedDon') => ({
  check,
  zone: 'CostArea',
  scope: 'Player',
  count: 1,
  name: '',
  filters: [],
});

export const createFilter = () => ({
  field: 'type',
  comparison: 'eq',
  value: 'Character',
});

/** Default comparison/value when switching filter field in the editor. */
export const getFilterFieldDefaults = (field, { traitSuggestions = [], nameSuggestions = [] } = {}) => {
  if (isBooleanFilterField(field)) {
    return {
      comparison: field === 'isRested' ? 'eq' : '',
      value: true,
    };
  }
  if (field === 'cardTraits') {
    return {
      comparison: 'contains',
      value: traitSuggestions[0] || '',
    };
  }
  if (field === 'name') {
    return {
      comparison: 'eq',
      value: nameSuggestions[0] || '',
    };
  }
  if (field === 'cost' || field === 'power') {
    return {
      comparison: 'lte',
      value: 1,
    };
  }
  if (field === 'type') {
    return {
      comparison: 'eq',
      value: 'Character',
    };
  }
  if (field === 'location') {
    return {
      comparison: 'eq',
      value: 'Hand',
    };
  }
  if (field === 'instanceId') {
    return {
      comparison: 'neqRef',
      ref: '',
      value: '',
    };
  }
  return {
    comparison: 'eq',
    value: '',
  };
};

export const createTargetStep = (index = 0) => ({
  stepId: `chooseTarget${index || ''}`,
  type: 'target',
  targetingPlayer: 'Player',
  targetCriteria: {
    scope: 'Player',
    zone: 'FieldCharacters',
    maxTargets: 1,
    optional: true,
    filters: [],
  },
  prompt: 'Select a Target',
});

export const createActionStep = (index = 0) => ({
  stepId: `action${index || ''}`,
  type: 'action',
  actionType: 'ModifyPower',
  detailsJson: JSON.stringify(ACTION_DETAIL_TEMPLATES.ModifyPower, null, 2),
});

export const IF_STEP_ARRAY_LENGTH_COMPARISONS = [
  'greaterThan',
  'lessThan',
  'equal',
  'greaterThanOrEqual',
  'lessThanOrEqual',
];

export const IF_STEP_LEADER_TRAITS_COMPARISONS = ['includes', 'excludes'];

/** Check types supported by handleIfStep in effectService.js */
export const IF_STEP_CHECK_TYPES = [
  {
    id: 'arrayLength',
    label: 'arrayLength — prior step result count',
    fields: ['refId', 'comparison', 'value'],
    comparisons: IF_STEP_ARRAY_LENGTH_COMPARISONS,
    defaultComparison: 'equal',
    defaultValue: 1,
    valueLabel: 'Compare to',
    numericValue: true,
  },
  {
    id: 'leaderTraits',
    label: 'leaderTraits — leader has trait',
    fields: ['comparison', 'value'],
    comparisons: IF_STEP_LEADER_TRAITS_COMPARISONS,
    defaultComparison: 'includes',
    defaultValue: '',
    valueLabel: 'Trait name',
    numericValue: false,
  },
  {
    id: 'thisBattledCharacter',
    label: 'thisBattledCharacter — battled a character',
    fields: [],
  },
  {
    id: 'minRestedDon',
    label: 'minRestedDon — minimum rested DON!!',
    fields: ['value'],
    defaultValue: 1,
    valueLabel: 'Min rested DON!!',
    numericValue: true,
  },
  {
    id: 'handSizeLte',
    label: 'handSizeLte — hand size at most',
    fields: ['value'],
    defaultValue: 0,
    valueLabel: 'Max hand size',
    numericValue: true,
  },
  {
    id: 'minLife',
    label: 'minLife — minimum life cards',
    fields: ['value'],
    defaultValue: 1,
    valueLabel: 'Min life',
    numericValue: true,
  },
  {
    id: 'maxLife',
    label: 'maxLife — maximum life cards',
    fields: ['value'],
    defaultValue: 0,
    valueLabel: 'Max life',
    numericValue: true,
  },
  {
    id: 'targetMatchesFilters',
    label: 'targetMatchesFilters — prior target matches filters',
    fields: ['refId', 'filters'],
    filtersLabel: 'Target filters',
    filtersHint: 'All cards from the prior step must match these filters.',
  },
];

export const getIfStepCheckDef = (checkType) => {
  const known = IF_STEP_CHECK_TYPES.find((c) => c.id === checkType);
  if (known) return known;
  return {
    id: checkType,
    label: checkType,
    fields: ['refId', 'comparison', 'value'],
    numericValue: false,
    valueLabel: 'Value',
  };
};

export const getIfCheckDefaults = (checkType, { traitSuggestions = [] } = {}) => {
  const def = getIfStepCheckDef(checkType);
  let value = def.defaultValue ?? '';
  if (checkType === 'leaderTraits' && traitSuggestions.length > 0) {
    value = traitSuggestions[0];
  }
  return {
    check: def.id,
    refId: def.fields.includes('refId') ? 'chooseTarget' : '',
    comparison: def.defaultComparison || '',
    value,
    filters: def.fields.includes('filters') ? [] : undefined,
  };
};

export const createIfCheck = (checkType = 'arrayLength', options = {}) => {
  const defaults = getIfCheckDefaults(checkType, options);
  const result = {
    check: defaults.check,
    refId: defaults.refId,
    comparison: defaults.comparison,
    value: defaults.value,
  };
  if (defaults.filters !== undefined) result.filters = defaults.filters;
  return result;
};

export const createIfStep = () => ({
  stepId: 'checkCondition',
  type: 'if',
  checks: [createIfCheck('arrayLength')],
});

export const SEARCH_DECK_PLACE_REST = ['DeckBottom', 'DeckTop', 'Choose'];
export const SEARCH_DECK_PLACE_REST_NO_REORDER = ['DeckBottom', 'DeckTop'];

export const getSearchDeckPlaceRestOptions = (reorder = true) =>
  (reorder !== false ? SEARCH_DECK_PLACE_REST : SEARCH_DECK_PLACE_REST_NO_REORDER);

export const SEARCH_DECK_DEFAULT_TITLES = {
  title: 'Select a Card',
  subtitle: ' ',
};

export const SEARCH_DECK_FORCE_NO_SELECTION_TITLES = {
  title: 'Reorder the Cards',
  subtitle: 'Then Choose Placement',
};

export const createDefaultSearchDeckDetails = () => ({
  fullSearch: false,
  amount: 5,
  forceNoSelection: false,
  revealWhileSearching: false,
  reorder: true,
  placeRest: 'DeckBottom',
  filters: [],
});

export const createSearchDeckStep = () => ({
  stepId: 'searchDeck',
  type: 'searchDeck',
  title: SEARCH_DECK_DEFAULT_TITLES.title,
  subtitle: SEARCH_DECK_DEFAULT_TITLES.subtitle,
  searchDeckDetails: createDefaultSearchDeckDetails(),
});

export const importFiltersForEditor = (filters = []) =>
  filters.map((f) => ({
    field: f.field,
    comparison: f.comparison || '',
    value: Array.isArray(f.value) ? f.value.join(', ') : f.value ?? '',
    ref: f.ref || '',
  }));

export const parseSearchDeckDetails = (details = {}) => ({
  fullSearch: details.fullSearch === true,
  amount: details.amount ?? 1,
  forceNoSelection: details.forceNoSelection === true,
  revealWhileSearching: details.revealWhileSearching === true,
  reorder: details.reorder !== false,
  placeRest: details.placeRest || 'DeckBottom',
  filters: importFiltersForEditor(details.filters),
});

export const buildSearchDeckDetails = (details) => {
  const result = {};
  if (details.fullSearch) result.fullSearch = true;
  if (!details.fullSearch) result.amount = Number(details.amount) || 1;
  if (details.forceNoSelection) result.forceNoSelection = true;
  if (details.revealWhileSearching) result.revealWhileSearching = true;
  result.reorder = !!details.reorder;
  if (details.placeRest) result.placeRest = details.placeRest;
  if (details.filters?.length > 0) result.filters = details.filters.map(buildFilter);
  return result;
};

export const createDefaultReturnDonsDetails = () => ({
  amount: 1,
  prompt: '',
});

export const createReturnDonsStep = (index = 0) => ({
  stepId: `chooseDons${index || ''}`,
  type: 'returnDons',
  optional: true,
  returnDonsDetails: createDefaultReturnDonsDetails(),
});

export const createPromptStep = (index = 0) => ({
  stepId: `prompt${index || ''}`,
  type: 'prompt',
  prompt: 'Confirm?',
});

export const REVEAL_WHO_OPTIONS = ['Opponent', 'Player'];

/** Prior step types whose results can be referenced by reveal cardRefId. */
export const REVEAL_CARD_REF_STEP_TYPES = ['target', 'searchDeck'];

export const createDefaultRevealDetails = () => ({
  whoReveals: 'Opponent',
  cardRefId: 'chooseTarget',
});

export const createRevealStep = (index = 0) => ({
  stepId: `reveal${index || ''}`,
  type: 'reveal',
  revealDetails: createDefaultRevealDetails(),
});

export const parseRevealDetails = (details = {}) => ({
  whoReveals: details.whoReveals || 'Opponent',
  cardRefId: details.cardRefId || '',
});

export const buildRevealDetails = (details) => {
  const result = {};
  if (details.whoReveals) result.whoReveals = details.whoReveals;
  if (details.cardRefId) result.cardRefId = details.cardRefId;
  return result;
};

export const parseReturnDonsDetails = (details = {}) => ({
  amount: details.amount ?? 1,
  prompt: details.prompt || '',
});

export const buildReturnDonsDetails = (details) => {
  const result = { amount: Number(details.amount) || 1 };
  if (details.prompt?.trim()) result.prompt = details.prompt.trim();
  return result;
};

export const createEffect = () => ({
  trigger: 'OnPlay',
  description: '',
  restriction: '',
  format: 'steps',
  preConditions: [],
  effectDetails: createPassiveEffectDetails('StatModifier'),
  cannotBeKodDetails: createCannotBeKodDetails(),
  extraNameDetails: createExtraNameDetails(),
  steps: [createTargetStep(), createActionStep()],
});

const TRIGGER_PATTERNS = [
  { pattern: /\[Activate:\s*Main\]/i, trigger: 'ActivateMain' },
  { pattern: /\[When Attacking\]/i, trigger: 'WhenAttacking' },
  { pattern: /\[On Play\]/i, trigger: 'OnPlay' },
  { pattern: /\[On KO\]/i, trigger: 'OnKO' },
  { pattern: /When your opponent's Character is K\.O\.'d/i, trigger: 'OpponentsCharacterKOd' },
  { pattern: /\[On Block\]/i, trigger: 'OnBlock' },
  { pattern: /\[Counter\]/i, trigger: 'Counter' },
  { pattern: /\[Trigger\]/i, trigger: 'Trigger' },
  { pattern: /\[Blocker\]/i, trigger: 'Blocker' },
  { pattern: /\[Rush\]/i, trigger: 'Rush' },
  { pattern: /\[Banish\]/i, trigger: 'Banish' },
  { pattern: /\[Double Attack\]/i, trigger: 'DoubleAttack' },
  { pattern: /\[End of Your Turn\]/i, trigger: 'EndOfYourTurn' },
  { pattern: /\[End of Battle\]/i, trigger: 'EndOfBattle' },
  { pattern: /\[Main\]/i, trigger: 'OnPlay' },
  { pattern: /can also attack your opponent's active Characters/i, trigger: 'CanAttackActiveCharacters' },
  { pattern: /cannot be K\.O/i, trigger: 'CannotBeKod' },
  { pattern: /cannot be attacked/i, trigger: 'CannotBeAttacked' },
  { pattern: /cannot attack/i, trigger: 'CannotAttack' },
  { pattern: /\[DON!!\s*x?\d*\]/i, trigger: 'Passive' },
];

export const getFirstEffectTextSegment = (effectText = '') => {
  const trimmed = effectText.trim();
  if (!trimmed) return '';
  return trimmed.split(/\n\n+/)[0].trim();
};

/** Split card effect text into separate abilities (by \\r\\n or blank lines). */
export const splitEffectTextIntoSegments = (effectText = '') =>
  effectText
    .split(/\r\n|\n\n+/)
    .map((segment) => segment.trim())
    .filter(Boolean);

const createEffectFromTextSegment = (segment) => {
  const trigger = inferTriggerFromEffectText(segment);
  return {
    ...createEffect(),
    trigger,
    description: segment,
    restriction: /\[Once Per Turn\]/i.test(segment) ? 'OncePerTurn' : '',
    format: inferEffectFormat(trigger),
    steps: [],
  };
};

/** Extract trait names from effect text, e.g. "{Straw Hat Crew}" → "Straw Hat Crew". */
export const extractTraitsFromEffectText = (effectText = '') => {
  const traits = new Set();
  const pattern = /\{([^}]+)\}/g;
  let match = pattern.exec(effectText);
  while (match) {
    const trait = match[1].trim();
    if (trait) traits.add(trait);
    match = pattern.exec(effectText);
  }
  return [...traits].sort((a, b) => a.localeCompare(b));
};

const BRACKET_KEYWORD_CONTENT = new Set([
  'Activate: Main',
  'When Attacking',
  'On Play',
  'On KO',
  'On K.O.',
  'On Block',
  'Counter',
  'Trigger',
  'Blocker',
  'Rush',
  'Banish',
  'Double Attack',
  'End of Your Turn',
  'End of Battle',
  'Main',
  'Your Turn',
  'Once Per Turn',
  'Play this card.',
]);

const isEffectKeywordBracket = (content) => {
  const normalized = content.trim();
  if (!normalized) return true;
  if (BRACKET_KEYWORD_CONTENT.has(normalized)) return true;
  if (/^DON!!(\s*x\s*\d+|-\d+)?$/i.test(normalized)) return true;
  return false;
};

/** Extract card/leader name references from effect text, e.g. "[Kozuki Oden]" → "Kozuki Oden". */
export const extractCardNamesFromEffectText = (effectText = '') => {
  const names = new Set();
  const pattern = /\[([^\]]+)\]/g;
  let match = pattern.exec(effectText);
  while (match) {
    const name = match[1].trim();
    if (name && !isEffectKeywordBracket(name)) names.add(name);
    match = pattern.exec(effectText);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
};

export const getPreConditionCheckDefaults = (check, { nameSuggestions = [] } = {}) => {
  const def = PRE_CONDITION_CHECKS.find((c) => c.id === check);
  const defaults = {};
  if (def?.fields.includes('zone')) defaults.zone = 'CostArea';
  if (def?.fields.includes('scope')) defaults.scope = 'Player';
  if (def?.fields.includes('count')) defaults.count = 1;
  if (def?.fields.includes('name')) defaults.name = nameSuggestions[0] || '';
  if (def?.fields.includes('filters')) defaults.filters = [];
  return defaults;
};

export const inferTriggerFromEffectText = (effectText = '') => {
  const segment = getFirstEffectTextSegment(effectText);
  const match = TRIGGER_PATTERNS.find(({ pattern }) => pattern.test(segment));
  return match?.trigger || 'OnPlay';
};

const inferEffectFormat = (trigger) => {
  if (['Blocker', 'Rush', 'Banish', 'DoubleAttack', 'CanAttackActiveCharacters', 'CannotBeKod', 'CannotBeAttacked', 'CannotAttack', 'ExtraName'].includes(trigger)) {
    return 'keyword';
  }
  if (trigger === 'Passive') return 'passive';
  return 'steps';
};

export const createEffectFromCard = (card) => {
  const segments = splitEffectTextIntoSegments(card?.effectText || '');
  if (segments.length === 0) return { ...createEffect(), steps: [] };
  return createEffectFromTextSegment(segments[0]);
};

export const createEffectsFromCard = (card) => {
  const segments = splitEffectTextIntoSegments(card?.effectText || '');
  if (segments.length === 0) return [{ ...createEffect(), steps: [] }];
  return segments.map(createEffectFromTextSegment);
};

export const createCardEntry = (cardOrId = '') => {
  if (typeof cardOrId === 'string') {
    return {
      cardId: cardOrId,
      effects: [{ ...createEffect(), steps: [] }],
    };
  }

  return {
    cardId: cardOrId.id || '',
    effects: createEffectsFromCard(cardOrId),
  };
};

export const parseDetailsJson = (json) => {
  if (!json?.trim()) return undefined;
  try {
    return JSON.parse(json);
  } catch {
    throw new Error('Invalid action details JSON');
  }
};

export const buildPreCondition = (pc) => {
  const def = PRE_CONDITION_CHECKS.find((c) => c.id === pc.check);
  const result = { check: pc.check };
  if (def?.fields.includes('zone')) result.zone = pc.zone || 'CostArea';
  if (def?.fields.includes('scope')) result.scope = pc.scope || 'Player';
  if (def?.fields.includes('count')) result.count = Number(pc.count) || 1;
  if (def?.fields.includes('name') && pc.name?.trim()) result.name = pc.name.trim();
  if (def?.fields.includes('filters') && pc.filters?.length > 0) {
    result.filters = pc.filters.map(buildFilter);
  }
  return result;
};

export const buildFilter = (filter) => {
  const result = { field: filter.field };
  if (isBooleanFilterField(filter.field)) {
    const boolValue = parseBooleanFilterValue(filter.value, true);
    if (filter.field === 'isRested') {
      result.comparison = filter.comparison || 'eq';
      result.value = boolValue;
      return result;
    }
    result.value = boolValue;
    return result;
  }
  if (filter.comparison) result.comparison = filter.comparison;
  if (filter.comparison === 'oneOf') {
    const traits = typeof filter.value === 'string'
      ? filter.value.split(',').map((s) => s.trim()).filter(Boolean)
      : filter.value;
    result.value = traits;
  } else if (FILTER_REF_COMPARISONS.includes(filter.comparison)) {
    result.ref = filter.ref || '';
  } else if (filter.value !== '' && filter.value !== undefined) {
    const num = Number(filter.value);
    result.value = Number.isNaN(num) ? filter.value : num;
  }
  return result;
};

export const buildTargetCriteria = (criteria) => {
  const zone = criteria.zone.includes(',')
    ? criteria.zone.split(',').map((z) => z.trim()).filter(Boolean)
    : criteria.zone;
  return {
    scope: criteria.scope,
    zone,
    maxTargets: Number(criteria.maxTargets) || 1,
    optional: criteria.optional,
    filters: criteria.filters.map(buildFilter),
  };
};

export const buildStep = (step) => {
  if (step.type === 'target') {
    const result = {
      stepId: step.stepId,
      type: 'target',
      targetCriteria: buildTargetCriteria(step.targetCriteria),
    };
    if (step.prompt?.trim()) result.prompt = step.prompt;
    if (step.targetingPlayer) result.targetingPlayer = step.targetingPlayer;
    return result;
  }

  if (step.type === 'action') {
    const result = {
      stepId: step.stepId,
      type: 'action',
      actionType: step.actionType,
    };
    const details = parseDetailsJson(step.detailsJson);
    if (details && Object.keys(details).length > 0) result.details = details;
    return result;
  }

  if (step.type === 'if') {
    return {
      stepId: step.stepId,
      type: 'if',
      checks: step.checks.map((c) => {
        const def = getIfStepCheckDef(c.check);
        const result = { check: c.check };
        if (def.fields.includes('refId') && c.refId) result.refId = c.refId;
        if (def.fields.includes('comparison') && c.comparison) result.comparison = c.comparison;
        if (def.fields.includes('value') && c.value !== '' && c.value !== undefined) {
          result.value = def.numericValue ? (Number(c.value) || 0) : c.value;
        }
        if (def.fields.includes('filters') && c.filters?.length > 0) {
          result.filters = c.filters.map(buildFilter);
        }
        return result;
      }),
    };
  }

  if (step.type === 'searchDeck') {
    const result = {
      stepId: step.stepId,
      type: 'searchDeck',
      title: step.title || 'Select a Card',
      subtitle: step.subtitle ?? ' ',
    };
    const details = buildSearchDeckDetails(step.searchDeckDetails || parseSearchDeckDetails());
    if (Object.keys(details).length > 0) result.details = details;
    return result;
  }

  if (step.type === 'returnDons') {
    const result = {
      stepId: step.stepId,
      type: 'returnDons',
      details: buildReturnDonsDetails(step.returnDonsDetails || parseReturnDonsDetails()),
    };
    if (step.optional) result.optional = true;
    return result;
  }

  if (step.type === 'prompt') {
    const result = {
      stepId: step.stepId,
      type: 'prompt',
    };
    if (step.prompt?.trim()) result.prompt = step.prompt.trim();
    return result;
  }

  if (step.type === 'reveal') {
    return {
      stepId: step.stepId,
      type: 'reveal',
      details: buildRevealDetails(step.revealDetails || parseRevealDetails()),
    };
  }

  return step;
};

export const buildEffect = (effect) => {
  const result = {
    trigger: effect.trigger,
    description: effect.description,
  };

  if (effect.restriction) result.restriction = effect.restriction;

  if (effect.preConditions.length > 0) {
    result.preConditions = effect.preConditions.map(buildPreCondition);
  }

  if (effect.format === 'passive') {
    const type = effect.effectDetails.type || 'StatModifier';
    result.effectDetails = {
      type,
      scope: effect.effectDetails.scope,
      filters: effect.effectDetails.filters.map(buildFilter),
    };
    if (type === 'StatModifier') {
      result.effectDetails.modifications = (effect.effectDetails.modifications || []).map((m) => ({
        stat: m.stat,
        amount: Number(m.amount) || 0,
      }));
      if (effect.effectDetails.multipliers?.length > 0) {
        result.effectDetails.multipliers = effect.effectDetails.multipliers
          .map(buildPassiveMultiplier)
          .filter(Boolean);
      }
    } else if (type === 'AddEffect') {
      const grantedRaw = effect.effectDetails.effect || { trigger: 'Rush' };
      result.effectDetails.effect = buildAddEffectNestedEffect(parseAddEffectNestedEffect(grantedRaw));
    }
    return result;
  }

  if (effect.format === 'steps') {
    result.version = 2;
    result.effectSteps = effect.steps.map(buildStep);
    return result;
  }

  if (effect.trigger === 'CannotBeKod' && effect.cannotBeKodDetails?.type) {
    applyCannotBeKodFields(result, effect.cannotBeKodDetails);
  }

  if (effect.trigger === 'ExtraName') {
    applyExtraNameFields(result, effect.extraNameDetails);
  }

  return result;
};

export const buildMultiCardEffectsJson = (cards) => {
  const payload = {};
  for (const card of cards) {
    const id = card.cardId?.trim();
    if (!id) continue;
    payload[id] = card.effects.map(buildEffect);
  }
  return JSON.stringify(payload, null, 2);
};

export const buildCardEffectsJson = (cardId, effects) =>
  buildMultiCardEffectsJson([{ cardId, effects }]);

export const getDuplicateCardIds = (cards) => {
  const seen = new Set();
  const duplicates = new Set();
  for (const card of cards) {
    const id = card.cardId?.trim();
    if (!id) continue;
    if (seen.has(id)) duplicates.add(id);
    seen.add(id);
  }
  return [...duplicates];
};

export const detectEffectFormat = (effect) => {
  if (effect.effectSteps?.length || effect.action) return 'steps';
  if (effect.effectDetails) return 'passive';
  return 'keyword';
};

export const isKeywordNestedEffect = (raw) => {
  if (!raw || typeof raw !== 'object') return true;
  return detectEffectFormat(raw) === 'keyword';
};

const mapImportedSteps = (rawSteps) =>
  rawSteps.map((step, index) => {
    if (step.type === 'target') {
      const zone = Array.isArray(step.targetCriteria?.zone)
        ? step.targetCriteria.zone.join(', ')
        : step.targetCriteria?.zone || 'FieldCharacters';
      return {
        stepId: step.stepId || `chooseTarget${index}`,
        type: 'target',
        prompt: step.prompt || 'Select a Target',
        targetingPlayer: step.targetingPlayer || 'Player',
        targetCriteria: {
          scope: step.targetCriteria?.scope || 'Player',
          zone,
          maxTargets: step.targetCriteria?.maxTargets ?? 1,
          optional: step.targetCriteria?.optional ?? true,
          filters: (step.targetCriteria?.filters || []).map((f) => ({
            field: f.field,
            comparison: f.comparison || '',
            value: Array.isArray(f.value) ? f.value.join(', ') : f.value ?? '',
            ref: f.ref || '',
          })),
        },
      };
    }

    if (step.type === 'action') {
      return {
        stepId: step.stepId || `action${index}`,
        type: 'action',
        actionType: step.actionType,
        detailsJson: step.details ? JSON.stringify(step.details, null, 2) : '{}',
      };
    }

    if (step.type === 'if') {
      return {
        stepId: step.stepId || `check${index}`,
        type: 'if',
        checks: (step.checks || []).map((c) => {
          const def = getIfStepCheckDef(c.check);
          const result = { check: c.check };
          if (def.fields.includes('refId')) result.refId = c.refId || '';
          if (def.fields.includes('comparison')) {
            result.comparison = c.comparison || def.defaultComparison || 'equal';
          }
          if (def.fields.includes('value')) {
            result.value = c.value ?? def.defaultValue ?? '';
          }
          if (def.fields.includes('filters')) {
            result.filters = importFiltersForEditor(c.filters);
          }
          return result;
        }),
      };
    }

    if (step.type === 'searchDeck') {
      return {
        stepId: step.stepId || 'searchDeck',
        type: 'searchDeck',
        title: step.title || 'Select a Card',
        subtitle: step.subtitle ?? ' ',
        searchDeckDetails: parseSearchDeckDetails(step.details),
      };
    }

    if (step.type === 'returnDons') {
      return {
        stepId: step.stepId || 'chooseDons',
        type: 'returnDons',
        optional: step.optional ?? false,
        returnDonsDetails: parseReturnDonsDetails(step.details),
      };
    }

    if (step.type === 'prompt') {
      return {
        stepId: step.stepId || 'prompt',
        type: 'prompt',
        prompt: step.prompt || '',
      };
    }

    if (step.type === 'reveal' || step.type === 'reveall') {
      return {
        stepId: step.stepId || `reveal${index}`,
        type: 'reveal',
        revealDetails: parseRevealDetails(step.details),
      };
    }

    return createActionStep(index);
  });

export const normalizeEffect = (effect) => {
  if (effect.format === 'legacyAction' && effect.legacyAction) {
    return {
      ...effect,
      format: 'steps',
      steps: [{
        stepId: 'action',
        type: 'action',
        actionType: effect.legacyAction.type,
        detailsJson: effect.legacyAction.detailsJson || '{}',
      }],
    };
  }
  return effect;
};

export const importEffect = (raw) => {
  const format = detectEffectFormat(raw);
  const effect = createEffect();
  effect.trigger = raw.trigger || 'OnPlay';
  effect.description = raw.description || '';
  effect.restriction = raw.restriction || '';
  effect.format = format;
  effect.preConditions = (raw.preConditions || []).map((pc) => ({
    check: pc.check,
    zone: pc.zone || 'CostArea',
    scope: pc.scope || 'Player',
    count: pc.count ?? 1,
    name: pc.name || '',
    filters: (pc.filters || []).map((f) => ({
      field: f.field,
      comparison: f.comparison || '',
      value: Array.isArray(f.value) ? f.value.join(', ') : f.value ?? '',
      ref: f.ref || '',
    })),
  }));

  if (format === 'passive' && raw.effectDetails) {
    const type = raw.effectDetails.type || 'StatModifier';
    effect.effectDetails = {
      type,
      scope: raw.effectDetails.scope || 'Player',
      filters: (raw.effectDetails.filters || []).map((f) => ({
        field: f.field,
        comparison: f.comparison || '',
        value: Array.isArray(f.value) ? f.value.join(', ') : f.value ?? '',
        ref: f.ref || '',
      })),
    };
    if (type === 'AddEffect') {
      effect.effectDetails.effect = raw.effectDetails.effect || { trigger: 'Rush' };
    } else {
      effect.effectDetails.modifications = (raw.effectDetails.modifications || [{ stat: 'power', amount: 1000 }]).map((m) => ({
        stat: m.stat,
        amount: m.amount,
      }));
      effect.effectDetails.multipliers = (raw.effectDetails.multipliers || []).map(importPassiveMultiplier);
    }
    effect.steps = [];
  }

  if (format === 'steps') {
    if (raw.effectSteps?.length) {
      effect.steps = mapImportedSteps(raw.effectSteps);
    } else if (raw.action) {
      effect.steps = [{
        stepId: 'action',
        type: 'action',
        actionType: raw.action.type,
        detailsJson: raw.action.details ? JSON.stringify(raw.action.details, null, 2) : '{}',
      }];
    }
  }

  if (format === 'keyword') {
    effect.steps = [];
  }

  if (raw.type || raw.attackerFilters?.length || raw.trigger === 'CannotBeKod') {
    effect.cannotBeKodDetails = {
      type: raw.type || '',
      attackerFilters: (raw.attackerFilters || []).map((f) => ({
        field: f.field,
        comparison: f.comparison || '',
        value: Array.isArray(f.value) ? f.value.join(', ') : f.value ?? '',
        ref: f.ref || '',
      })),
    };
  }

  if (raw.details?.name || raw.trigger === 'ExtraName') {
    effect.extraNameDetails = { name: raw.details?.name || '' };
  }

  return effect;
};

export const parseAddEffectNestedEffect = (raw) =>
  raw ? importEffect(raw) : { ...createEffect(), format: 'keyword', trigger: 'Rush', steps: [] };

export const buildAddEffectNestedEffect = (editorEffect) => {
  if (editorEffect.format === 'keyword') {
    const result = { trigger: editorEffect.trigger || 'Rush' };
    if (editorEffect.trigger === 'CannotBeKod') {
      applyCannotBeKodFields(result, editorEffect.cannotBeKodDetails);
    }
    if (editorEffect.trigger === 'ExtraName') {
      applyExtraNameFields(result, editorEffect.extraNameDetails);
    }
    return result;
  }
  return buildEffect(editorEffect);
};

export const importEffectsJson = (jsonText) => {
  const parsed = JSON.parse(jsonText);
  const entries = Object.entries(parsed);
  if (entries.length === 0) throw new Error('JSON must contain at least one card entry');

  return entries.map(([cardId, effectsArray]) => {
    if (!Array.isArray(effectsArray)) {
      throw new Error(`Entry for "${cardId}" must be an array of effects`);
    }
    return {
      cardId,
      effects: effectsArray.map(importEffect),
    };
  });
};

export const importCardEffectsJson = (jsonText) => {
  const cards = importEffectsJson(jsonText);
  return cards[0];
};

export const mergeCards = (existingCards, importedCards, { replaceExisting = true } = {}) => {
  const next = existingCards.map((card) => ({
    cardId: card.cardId,
    effects: card.effects.map((effect) => ({ ...effect })),
  }));

  for (const imported of importedCards) {
    const index = next.findIndex((c) => c.cardId === imported.cardId);
    if (index >= 0 && replaceExisting) {
      next[index] = {
        cardId: imported.cardId,
        effects: imported.effects.map((effect) => ({ ...effect })),
      };
    } else if (index < 0) {
      next.push({
        cardId: imported.cardId,
        effects: imported.effects.map((effect) => ({ ...effect })),
      });
    }
  }

  return next;
};

export const serializeWorkspace = (cards) => ({
  version: 1,
  savedAt: new Date().toISOString(),
  cards: cards.map((card) => ({
    cardId: card.cardId,
    effects: card.effects,
  })),
});

export const deserializeWorkspace = (data) => {
  if (!data?.cards?.length) throw new Error('Save slot is empty or invalid');
  return data.cards.map((card) => ({
    cardId: card.cardId || 'CARD-ID',
    effects: (card.effects || [createEffect()]).map((effect) => normalizeEffect({ ...effect })),
  }));
};
